import React, { useState } from 'react';
import { ArrowRightIcon, ChevronDownIcon, MessageSquareQuoteIcon } from 'lucide-react';
import { SmartLink } from '../components/SmartLink';
import { FeaturedStory } from '../components/stories/FeaturedStory';
import { StoryCard } from '../components/stories/StoryCard';
import { VideoRow } from '../components/stories/VideoRow';
import { StatsRow } from '../components/company/StatsRow';
import { CtaBand } from '../components/company/CtaBand';
import { PlaceholderNote } from '../components/company/PlaceholderNote';
import { FilterChips } from '../components/whatsnew/FilterChips';
import { customerStories, featuredStorySlug, storyLibraryStats } from '../data/stories';
import { solutionCatalog } from '../data/solutions/catalog';
import { productCatalog } from '../data/products/shared';
const PAGE_SIZE = 9;
const industryOptions = ['All', ...Object.values(solutionCatalog).filter(s => s.kind === 'industry').map(s => s.title)];
export function CustomerStories() {
  const [industry, setIndustry] = useState('All');
  const [product, setProduct] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const featured = customerStories.find(s => s.slug === featuredStorySlug) ?? customerStories[0];
  const filtering = industry !== 'All' || product !== '';
  const filtered = customerStories.filter(s => {
    if (!filtering && s.slug === featured.slug) return false;
    if (industry !== 'All' && solutionCatalog[s.industry]?.title !== industry) return false;
    if (product && !s.products.includes(product)) return false;
    return true;
  });
  const shown = filtered.slice(0, visible);
  const videos = customerStories.filter(s => s.video).slice(0, 3);
  const reset = () => {
    setIndustry('All');
    setProduct('');
    setVisible(PAGE_SIZE);
  };
  return <main data-id="element-1940" data-mp="3bc7jck2aaupl">
      <section className="hero-surface w-full" aria-labelledby="stories-heading" data-id="element-1941" data-mp="3bbo3ra03wieq">
        <div className="mx-auto max-w-shell px-6 pb-16 pt-14 lg:pb-20 lg:pt-20" data-id="element-1942" data-mp="36dmicif7k703">
          <div className="max-w-[760px]" data-id="element-1943" data-mp="36bjti1pskjwy">
            <h1 id="stories-heading" className="display text-[38px] font-semibold text-ink sm:text-[50px] lg:text-[56px]" data-id="element-1944" data-mp="3w0gxzr70uzs1">
              Businesses across Rwanda run on Kayko
            </h1>
            <p className="mt-5 max-w-[560px] text-[18px] leading-[1.5] text-ink-muted" data-id="element-1945" data-mp="8o664l4i49of">
              See how owners like you sell more, waste less, and grow with confidence.
            </p>
          </div>
          <div className="mt-12" data-id="element-1946" data-mp="3d3pilsulp4t9">
            <FeaturedStory story={featured} data-id="element-1947" data-mp="3d4h4gbyebo6c" />
          </div>
        </div>
      </section>

      <StatsRow stats={storyLibraryStats} label="Kayko customers in numbers" data-id="element-1948" data-mp="3h4j5ciwb4dkk" />

      <section className="w-full bg-white pb-20" aria-labelledby="all-stories-heading" data-id="element-1949" data-mp="3m2kn90hzy6ug">
        <div className="mx-auto max-w-shell px-6" data-id="element-1950" data-mp="3r3gpzaqm3mya">
          <h2 id="all-stories-heading" className="display text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-1951" data-mp="3r02oe4prvhrw">
            All stories
          </h2>

          <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-1952" data-mp="16b57382883s4">
            <div className="min-w-0 lg:[&>div]:justify-start" data-id="element-1953" data-mp="2qt7b1244mcfg">
              <FilterChips options={industryOptions} value={industry} onChange={v => {
              setIndustry(v);
              setVisible(PAGE_SIZE);
            }} ariaLabel="Filter stories by industry" data-id="element-1954" data-mp="2lvfkocj4qydd" />
            </div>
            <div className="relative shrink-0" data-id="element-1955" data-mp="28410i2dhkr4q">
              <label htmlFor="story-product" className="sr-only" data-id="element-1956" data-mp="281o8sxhxf2eh">
                Filter by product
              </label>
              <select id="story-product" value={product} onChange={e => {
              setProduct(e.target.value);
              setVisible(PAGE_SIZE);
            }} className="h-11 w-full appearance-none rounded-[10px] border border-ink/15 bg-white pl-4 pr-10 text-[15px] font-medium text-ink outline-none focus:border-brand lg:w-[220px]" data-id="element-1957" data-mp="1y5l8lx6mxdu6">
                <option value="" data-id="element-1958" data-mp="3vkxlftg49nkm">All products</option>
                {Object.entries(productCatalog).map(([key, p]) => <option key={key} value={key} data-id="element-1959" data-mp="4ee5y0zc59fz">
                    {p.title}
                  </option>)}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-3.5 h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-1960" data-mp="3qluiqfnlztft" />
            </div>
          </div>

          <PlaceholderNote className="mt-6 max-w-[520px]" data-id="element-1961" data-mp="3ctejlpxtwfk6">
            Result numbers are placeholders until confirmed by each customer.
          </PlaceholderNote>

          {shown.length > 0 ? <ul className="mt-10 grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-2 lg:grid-cols-3" data-id="element-1962" data-mp="32ufbq9kw0ny8">
              {shown.map(story => <li key={story.slug} data-id="element-1963" data-mp="2tvbs94ht66j7">
                  <StoryCard story={story} data-id="element-1964" data-mp="2twdgkanu4piv" />
                </li>)}
            </ul> : <div className="mx-auto mt-10 max-w-[480px] rounded-[14px] border border-canvas-line px-6 py-10 text-center" data-id="element-1965" data-mp="2xwnjtfk8t1u1">
              <p className="text-[16px] font-semibold text-ink" data-id="element-1966" data-mp="2fazja47drc1h">No stories match these filters yet</p>
              <p className="mt-2 text-[14px] text-ink-muted" data-id="element-1967" data-mp="2o9tdjnx8nqbv">Try another industry or product.</p>
              <button type="button" onClick={reset} className="mt-5 text-[15px] font-semibold text-brand transition-colors duration-150 ease-out hover:text-brand-hover" data-id="element-1968" data-mp="2jaq7q19fpg2u">
                Clear filters
              </button>
            </div>}

          {filtered.length > visible && <div className="mt-12 flex justify-center" data-id="element-1969" data-mp="36zsz40tvenaj">
              <button type="button" onClick={() => setVisible(v => v + PAGE_SIZE)} className="inline-flex h-12 items-center rounded-[7px] border border-ink/20 bg-white px-6 text-[15px] font-semibold text-ink transition-colors duration-150 ease-out hover:border-ink/45" data-id="element-1970" data-mp="32yz82yl4ssdf">
                Load more
              </button>
            </div>}
        </div>
      </section>

      <VideoRow stories={videos} data-id="element-1971" data-mp="2do4d4h7z9but" />

      <section className="w-full bg-white py-16" aria-label="Share your story" data-id="element-1972" data-mp="2dl8cum8nqb37">
        <div className="mx-auto max-w-shell px-6" data-id="element-1973" data-mp="2ilctqddh97ty">
          <div className="flex flex-col gap-4 rounded-[14px] border border-canvas-line px-6 py-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1974" data-mp="2ndnvzmvox5po">
            <p className="flex items-start gap-3" data-id="element-1975" data-mp="2selddx8vlvq4">
              <MessageSquareQuoteIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1976" data-mp="2xasmu00hdpct" />
              <span data-id="element-1977" data-mp="31e8gcnh979la">
                <span className="block text-[16px] font-semibold text-ink" data-id="element-1978" data-mp="31doyf6rlsl79">Want to share your story?</span>
                <span className="mt-1 block text-[14px] text-ink-muted" data-id="element-1979" data-mp="36bh0uupvjppp">
                  Tell us how Kayko helps your business — we’d love to feature you.
                </span>
              </span>
            </p>
            <SmartLink href="/contact" className="group inline-flex shrink-0 items-center gap-1.5 pl-8 text-[15px] font-semibold text-brand transition-colors duration-150 ease-out hover:text-brand-hover sm:pl-0" data-id="element-1980" data-mp="1c0bql7ka8f1e">
              Get in touch
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1981" data-mp="up6fzuee8a3w" />
            </SmartLink>
          </div>
        </div>
      </section>

      <CtaBand headline="Join thousands of businesses on Kayko" subheadline="Set up in minutes. Local support in Kigali when you need it." primary={{
      label: 'Get started',
      href: '/get-started'
    }} secondary={{
      label: 'Book a demo',
      href: '/demo'
    }} data-id="element-1982" data-mp="1nbhri03jfvfh" />
    </main>;
}